import React, { Fragment, } from 'react';
import { sumBy, orderBy, chunk, groupBy, get, pick, isEmpty, keyBy, omit, } from 'lodash';
import numeral from 'numeral';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, } from 'reactstrap';
import { toast } from 'react-toastify';
import { addDays, startOfDay, parse as parseDate, isValid as isValidDate, format as formatDate } from 'date-fns';
import { useToggle, useList, } from 'react-use';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import fileDownload from 'js-file-download';
import dedent from 'dedent';

import firebase, { functions } from '../../firebase';
import { fields, getCombinedItems, } from '../../shared/models/combiningTemplate';
import { smallImageUrl, } from '../../shared/models/amazonProduct';
import { idWithTenant, } from '../../shared/util';
import { deliveryTimes, prefectures, } from '../../shared/config';
import { fieldDisplayValue, } from '../../shared/util';
import { newId, getAddressByPostalCode, } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import Page from '../hocs/Page';
import CombiningTemplateFormModal from '../modals/CombiningTemplateFormModal';
import AddButton from '../AddButton';
import ImportButton from '../ImportButton';
import ExportButton from '../ExportButton';
import QuerySelector from '../QuerySelector';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import FilesUploadButton from '../FilesUploadButton';
import QuerySortLabel from '../QuerySortLabel';
import CombiningsTabs from '../CombiningsTabs';
import ProductTagsDisplay from '../ProductTagsDisplay';

const { values, keys, entries } = Object;

export default Page(function CombiningTemplates (props) {
  const { tenant } = props;
  const queryParams = useQueryParams();
  const {
    combiningOptions: combiningOptionsForFilter = [],
    newItems,
    sort = { field: 'id', direction: 'desc', },
  } = queryParams;
  const products = useCollectionSubscription(tenant.ref.collection('products').orderBy('createdAt'), [tenant.id]);
  const productsById = keyBy(products, 'id');
  const productsBySku = keyBy(products, 'sku');
  // 同梱は実物を扱うため、実在庫を持たないセット品は選択肢から除く
  const selectableProducts = products.filter(_ => !_.isSet);
  const combiningTemplatesRef = tenant.ref.collection('combiningTemplates');
  const combiningTemplates = useCollectionSubscription(combiningTemplatesRef.orderBy('createdAt', 'desc'), [tenant.id]);
  const combiningTemplateItems = combiningTemplates.map((combiningTemplate) => {
    const { combiningItems = [], items = [], senderId, status, } = combiningTemplate;
    const itemsWithProduct = items.map((item, i) => {
      const { productId, } = item;
      const product = productsById[productId];
      return { ...item, product };
    }).filter(_ => _.product);
    return {
      ...combiningTemplate,
      itemsWithProduct,
    };
  });

  const [lastCombiningTemplate] = useCollectionSubscription(combiningTemplatesRef.orderBy('createdAt', 'desc').limit(1), [tenant.id]);
  const newItemRef = combiningTemplatesRef.doc('C' + newId(lastCombiningTemplate));
  const processNewValues = async (values) => {
    return {
      ...values,
      status: 'initial',
      productIds: values.items.map(_ => _.productId),
    };
  };

  return (
    <div className="combining-requests container-fluid">
      <div className="p-4 bg-white my-4">
        <CombiningsTabs activeTab="combiningTemplates" />
        <div className="d-flex justify-content-start mb-2">
          <div className="d-flex align-items-center">
            <AddButton label="加工組登録" itemRef={combiningTemplatesRef.doc()} FormModal={CombiningTemplateFormModal} initialValues={{}} processValues={processNewValues} formProps={{ tenant, products: selectableProducts, }} />
          </div>
        </div>
        <div className="mt-3 overflow-auto position-relative" style={{ zIndex: 0 }}>
          {
            combiningTemplateItems.length > 0 ? (
              <table className="table sticky-table table-sm table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th style={{ minWidth: 80 }}>
                      加工組名
                    </th>
                    <th style={{ minWidth: 200 }}>加工元商品</th>
                    <th style={{ minWidth: 50 }}>1加工あたりの数量</th>
                    <th style={{ minWidth: 200 }}>完成商品</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    combiningTemplateItems.map((combiningTemplateItem) => {
                      const { id, ref, name, itemsWithProduct, } = combiningTemplateItem;
                      const rowItems = itemsWithProduct.length > 0 ? itemsWithProduct : [{}];
                      const processValues = (values) => {
                        return {
                          ...values,
                          productIds: values.items.map(_ => _.productId),
                        };
                      };
                      const combinedItems = getCombinedItems(combiningTemplateItem);

                      return (
                        <Fragment key={id}>
                          {
                            rowItems.map((item, i) => {
                              const { productId, product, } = item;

                              return (
                                <tr key={i}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          <div>{name}</div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>
                                    <ProductTagsDisplay product={product} productTags={[]} />
                                    <div className="text-muted small d-flex gap-1">
                                      <div>{product && product.sku}</div>
                                    </div>
                                    <div className="d-flex align-items-start gap-1">
                                      {
                                        product?.isAmazonProduct && (
                                          <div>
                                            <img src={smallImageUrl(product?.amazonProduct)} style={{ maxHeight: 40 }} />
                                          </div>
                                        )
                                      }
                                      <div className="flex-grow-1 text-wrap">
                                        <span>{product?.name}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    {numeral(item.quantity).format('0,0')}
                                  </td>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            combinedItems.map(({ productId, quantity }, ci) => {
                                              const combinedProduct = productsById[productId];
                                              return (
                                                <div key={ci} className={ci > 0 ? 'mt-2 pt-2 border-top' : ''}>
                                                  <ProductTagsDisplay product={combinedProduct} productTags={[]} />
                                                  <div className="text-muted small d-flex gap-1">
                                                    <div>{combinedProduct && combinedProduct.sku}</div>
                                                  </div>
                                                  <div className="d-flex align-items-start gap-1">
                                                    <div className="flex-grow-1 text-wrap">
                                                      <span>{combinedProduct?.name}</span>
                                                      <span className="text-muted"> × {numeral(quantity).format('0,0')}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div className="d-flex flex-column gap-1 text-nowrap">
                                            <EditButton size="sm" itemRef={ref} FormModal={CombiningTemplateFormModal} processValues={processValues} formProps={{ tenant, values: combiningTemplateItem, products: selectableProducts }} />
                                            <DeleteButton size="sm" item={combiningTemplateItem} itemRef={ref} />
                                          </div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                </tr>
                              );
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                加工組はありません
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
